import * as Sentry from '@sentry/browser'
import { mapObjIndexed } from 'ramda'
import { object, string } from 'yup'

import { ActionType, FunnelName, PageName } from 'generated/types'
import { sendToSentry, transformErrors } from 'utils/errors'
import { FlowRoute } from 'utils/FlowProvider/types'
import { getGeneralError } from 'utils/getGeneralError'

import { SuitabilityResponse } from '.'

export const validationSchema = props => {
  const schema = {}
  props.responses.map(response => {
    schema[response.question.id] = string().required('Please select an option')
  })
  return object().shape(schema)
}

// Handles Formik's validate event.
export const validate = (values, props) => {
  let errors = {}

  try {
    validationSchema(props).validateSync(values, { abortEarly: false })
  } catch (validationErrors) {
    // Convert validation errors to Formik errors collection
    errors = transformErrors(validationErrors.inner)
  }

  return errors
}

export const handleSubmit = async (values, { props, ...ownProps }) => {
  const input = []

  mapObjIndexed((answer, question) => {
    input.push({
      questionId: question,
      answerId: answer,
    })
  }, values)
  ownProps.setSubmitting(true)
  Sentry.addBreadcrumb({
    message: 'User is attempting to update their suitability questions.',
  })

  try {
    const {
      data: { updateSuitability },
    } = await props.updateSuitability({ variables: { input } })
    const portfolioId = updateSuitability.recommendedPortfolio.id
    await props.openCoreInvestmentAccount({ variables: { input: { portfolioId } } })
    const locationState = { portfolioId }
    if (props.isDynamicFunnelEnabled) {
      // We are doing custom logic like this in order to prepare for removing analyzing as a route
      // Analyzing should be an animation, not a separate page
      props.metadata.name === FunnelName.Default
        ? props.history.push(FlowRoute.Analyzing, locationState)
        : props.routeToPage(PageName.SuitabilityQuestions, ActionType.Continue)
    } else {
      props.route({ from: FlowRoute.SuitabilityQuestions, action: 'continue', locationState })
    }
  } catch (error) {
    const retryHandler = () => {
      props.closeErrorModal()
      ownProps.submitForm()
    }
    const { title, message, actions, image } = getGeneralError(error, props, retryHandler)
    props.showErrorModal({ title, message, actions, image })

    ownProps.setSubmitting(false)
    sendToSentry(error)
  }
}

export const mapPropsToValues = props => {
  const propValues = {}
  if (!props.loading && props.responses) {
    props.responses.map(response => {
      propValues[response.question.id] = ''
    })
  }
  return propValues
}

export const mapRowsAndQuestions = (responses: SuitabilityResponse[], values) => {
  const questions = responses
    ? responses.map(response => {
        return {
          label: response.question.text,
          name: response.question.id,
          options: response.question.possibleAnswers.map(answer => ({
            label: answer.text,
            value: answer.id,
            subtext: answer.description,
          })),
          type: 'select',
          useNative: false,
        }
      })
    : []

  return {
    questions,
    rows: getRows(questions),
    valid: Object.values(values).every(Boolean),
  }
}

export const getRows = questions => {
  // This function will take any number of questions and separate
  // them into a single array with arrays of two objects representing
  // a "row" in FormBuilder
  return questions.reduce(
    (acc, question) => {
      const latestRow = acc[acc.length - 1]

      if (latestRow.length < 2) {
        latestRow.push(question)
      } else {
        acc.push([question])
      }
      return acc
    },
    [[]],
  )
}
